import { useState } from 'react';
import { Delay, ShiftMetricsData } from '../../../shared/types/shiftMetricsData.ts';
import { Dialog } from '../../components/dialog-portal/dialog.tsx';
import { InfoIcon } from '../../components/dialog-portal/infoIcon.tsx';
import { trpc } from '../../utils/trpc/trpc.ts';
import { DelayDistributionBarHorizontal } from './delayDistributionBarHorizontal.tsx';

type DelaySortField = 'equipment' | 'delay' | 'delayFrom';
type DelaySortDirection = 'asc' | 'desc';

export function ShiftMetricsPanel() {
    const [isInfoOpen, setIsInfoOpen] = useState(false);
    const [infoTab, setInfoTab] = useState<'delays' | 'disconnected' | 'operators'>('delays');
    const [delaySort, setDelaySort] = useState<{ field: DelaySortField; direction: DelaySortDirection }>({
        field: 'delay',
        direction: 'desc',
    });

    const { data: shiftMetrics, isLoading } = trpc.shiftMetrics.getMetrics.useQuery<ShiftMetricsData>(undefined, {
        refetchInterval: 10_000,
    });
    const shouldShowOperatorFeatures = import.meta.env.APP_ENV !== 'production';

    if (isLoading || !shiftMetrics) {
        return (
            <div
                style={{
                    border: '4px solid #04aa6d',
                    padding: '16px 20px',
                    fontSize: 22,
                    lineHeight: 1.6,
                }}
            >
                Loading shift metrics...
            </div>
        );
    }

    function hours(millis: number) {
        const h = millis / 3600000; // 1000 * 60 * 60
        return `${h.toFixed(2)}h`;
    }

    const notAvailable = 'n/a';
    const totalMachines = shiftMetrics.totalMachines;
    const operationalMachines = shiftMetrics.operationalMachines;
    const activeShiftOperators = shiftMetrics.activeShiftOperators;
    const activeShiftOperatorInitials = shiftMetrics.activeShiftOperatorInitials ?? [];
    const plannedShiftOperatorInitials = shiftMetrics.plannedShiftOperatorInitials ?? [];
    const operatorActivity = shiftMetrics.operatorActivity ?? [];
    const oeeShiftAverage = shiftMetrics.oeeShiftAverage;
    const oeeCurrent = shiftMetrics.oeeCurrent;
    const potentialCurrentShiftCapacity = shiftMetrics.potentialCurrentShiftCapacity;
    const currentShiftCapacity = shiftMetrics.currentShiftCapacity;
    const disconnectedTotalShiftTime = shiftMetrics.disconnectedTotalShiftTime;
    const disconnectedMachines = shiftMetrics.disconnectedMachines ?? [];
    const delays = shiftMetrics.delaysArray;

    // The following are calculated in front end based on received data
    const runningPercent =
        totalMachines != null && totalMachines > 0 && operationalMachines != null ? Math.floor((operationalMachines / totalMachines) * 100) : null;
    const effectiveCapacityPercent =
        potentialCurrentShiftCapacity != null && potentialCurrentShiftCapacity > 0 && currentShiftCapacity != null
            ? Math.round((currentShiftCapacity / potentialCurrentShiftCapacity) * 100)
            : null;
    const disconnectedPercent = effectiveCapacityPercent != null ? 100 - effectiveCapacityPercent : null;

    // const delays: Delay[] = [
    //     { equipment: 'Machine A', delay: 5 },
    //     { equipment: 'Machine B', delay: 10 },
    //     { equipment: 'Machine C', delay: 20 },
    //     { equipment: 'Machine D', delay: 45 },
    // ];

    function calculateDelayDistributionSlices(delays: Delay[]): { label: string; percent: number; total: number; count: number; color: string }[] {
        const delayDistributionSlices = [
            { label: '> 60 min', percent: 0, total: 0, count: 0, color: '#d81900' },
            { label: '30-60 min', percent: 0, total: 0, count: 0, color: '#e08700' },
            { label: '15-30 min', percent: 0, total: 0, count: 0, color: '#d9d200' },
            { label: '< 15 min', percent: 0, total: 0, count: 0, color: '#04aa6d' },
        ];

        const addToSlice = (sliceIndex: number, delay: number) => {
            delayDistributionSlices[sliceIndex].total += delay;
            delayDistributionSlices[sliceIndex].count += 1;
        };

        // Allocate delays into the appropriate slices (delay is in minutes)
        delays.forEach((delay) => {
            if (delay.delay < 15) {
                addToSlice(3, delay.delay);
            } else if (delay.delay < 30) {
                addToSlice(2, delay.delay);
            } else if (delay.delay < 60) {
                addToSlice(1, delay.delay);
            } else {
                addToSlice(0, delay.delay);
            }
        });

        const totalDelayItems = delayDistributionSlices.reduce((sum, slice) => sum + slice.count, 0);

        // Calculate percentages by delay item count (not delay duration).
        delayDistributionSlices.forEach((slice) => {
            slice.percent = totalDelayItems > 0 ? Math.round((slice.count / totalDelayItems) * 100) : 0;
        });
        return delayDistributionSlices;
    }
    const delayDistributionSlices = delays ? calculateDelayDistributionSlices(delays) : [];

    function toggleDelaySort(field: DelaySortField) {
        setDelaySort((prev) => {
            if (prev.field === field) {
                return {
                    field,
                    direction: prev.direction === 'asc' ? 'desc' : 'asc',
                };
            }

            return {
                field,
                direction: field === 'equipment' ? 'asc' : 'desc',
            };
        });
    }

    function getSortIndicator(field: DelaySortField): string {
        if (delaySort.field !== field) return '';
        return delaySort.direction === 'asc' ? ' ▲' : ' ▼';
    }

    const sortedDelays = [...(delays ?? [])].sort((a, b) => {
        let comparison = 0;

        if (delaySort.field === 'equipment') {
            comparison = a.equipment.localeCompare(b.equipment);
        } else if (delaySort.field === 'delay') {
            comparison = a.delay - b.delay;
        } else {
            const aTime = a.delayFrom ? new Date(a.delayFrom).getTime() : null;
            const bTime = b.delayFrom ? new Date(b.delayFrom).getTime() : null;

            if (aTime == null && bTime == null) comparison = 0;
            else if (aTime == null) comparison = 1;
            else if (bTime == null) comparison = -1;
            else comparison = aTime - bTime;
        }

        return delaySort.direction === 'asc' ? comparison : -comparison;
    });

    const plannedSet = new Set(plannedShiftOperatorInitials);
    const activeSet = new Set(activeShiftOperatorInitials);
    const activityByInitials = new Map(operatorActivity.map((it) => [it.initials, it]));

    const plannedActiveInitials = plannedShiftOperatorInitials.filter((it) => activeSet.has(it));
    const plannedOnlyInitials = plannedShiftOperatorInitials.filter((it) => !activeSet.has(it));
    const unplannedActiveInitials = activeShiftOperatorInitials.filter((it) => !plannedSet.has(it));

    const plannedActiveCount = plannedActiveInitials.length;
    const unplannedActiveCount = unplannedActiveInitials.length;
    const plannedTotalCount = plannedShiftOperatorInitials.length;

    const operatorSummary =
        plannedTotalCount > 0
            ? `${plannedActiveCount}${unplannedActiveCount > 0 ? `+${unplannedActiveCount}` : ''}/${plannedTotalCount}`
            : (activeShiftOperators ?? notAvailable);

    const operatorStatusRows = [
        ...plannedActiveInitials.map((initials) => {
            const activity = activityByInitials.get(initials);
            return {
                initials,
                status: 'Active (Planned)',
                emphasize: false,
                muted: false,
                firstActionTime: activity?.firstActionTime ?? null,
                latestActionTime: activity?.latestActionTime ?? null,
                totalActions: activity?.totalActions ?? null,
            };
        }),
        ...plannedOnlyInitials.map((initials) => ({
            initials,
            status: 'Planned (No Activity)',
            emphasize: false,
            muted: true,
            firstActionTime: null,
            latestActionTime: null,
            totalActions: 0,
        })),
        ...unplannedActiveInitials.map((initials) => {
            const activity = activityByInitials.get(initials);
            return {
                initials,
                status: 'Active (Not Planned)',
                emphasize: true,
                muted: false,
                firstActionTime: activity?.firstActionTime ?? null,
                latestActionTime: activity?.latestActionTime ?? null,
                totalActions: activity?.totalActions ?? null,
            };
        }),
    ];

    return (
        <div
            style={{
                border: '4px solid #04aa6d',
                padding: '8px 15px 12px 15px',
                fontSize: 18,
                lineHeight: 1.6,
                display: 'flex',
                flexDirection: 'column',
                gap: 6,
            }}
        >
            <div>
                <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span>
                        Shift: {formatFull(shiftMetrics.shiftStart)} – {formatFull(shiftMetrics.shiftEnd)}
                    </span>
                    <InfoIcon onClick={() => setIsInfoOpen(true)} title='Shift Information' />
                </div>
                <hr />
                <div style={{ marginBottom: 8, display: 'flex', justifyContent: 'space-between' }}>
                    <span style={{ marginRight: 8 }}>
                        Running:{' '}
                        <strong>
                            {runningPercent != null ? `${runningPercent}%` : notAvailable} (
                            {totalMachines != null && operationalMachines != null ? `${operationalMachines}/${totalMachines}` : notAvailable})
                        </strong>
                    </span>
                    <span>
                        OEE Availability - Avg: <strong>{oeeShiftAverage != null ? `${oeeShiftAverage}%` : notAvailable}</strong> | Current:{' '}
                        <strong>{oeeCurrent != null ? `${oeeCurrent}%` : notAvailable}</strong>
                    </span>
                </div>
                <div style={{ marginBottom: 4, display: 'flex', justifyContent: 'space-between' }}>
                    <span>
                        Capacity:{' '}
                        <strong>
                            {currentShiftCapacity != null ? `${hours(currentShiftCapacity)}` : notAvailable} /{' '}
                            {potentialCurrentShiftCapacity != null ? `${hours(potentialCurrentShiftCapacity)}` : notAvailable} (
                            {effectiveCapacityPercent != null ? `${effectiveCapacityPercent}%` : notAvailable})
                        </strong>
                    </span>
                    <span>
                        Discon:{' '}
                        <strong>
                            {disconnectedTotalShiftTime != null ? `${hours(disconnectedTotalShiftTime)} (${disconnectedPercent}%)` : notAvailable}
                        </strong>
                    </span>
                    {shouldShowOperatorFeatures && (
                        <span>
                            Operators: <strong>{operatorSummary}</strong>
                        </span>
                    )}
                </div>
            </div>
            <div style={{ height: 45 }}>
                <DelayDistributionBarHorizontal slices={delayDistributionSlices} />
            </div>
            {isInfoOpen && (
                <Dialog title='Shift Metrics Info' onClose={() => setIsInfoOpen(false)}>
                    <div style={{ flex: '1 1 auto', minHeight: 0, overflowY: 'auto', paddingRight: 6 }}>
                        <h3>Running</h3>
                        <p>Running = machines in OPERATE or SETUP divided by machines with known status.</p>

                        <h3>Capacity</h3>
                        <p>Potential capacity = machine count × elapsed shift time.</p>
                        <p>Current capacity = sum of connected machine time from live OEE rows.</p>
                        <p>Discon = sum of realTime - connectedTime.</p>

                        {shouldShowOperatorFeatures && (
                            <>
                                <h3>Operators</h3>
                                <p>Panel shows planned-active + unplanned-active / planned-total.</p>
                                <p>Active = distinct operator initials saved to operations or maintenance operations in current shift.</p>
                                <p>Early actions by planned operators (NB! up to 2h before shift start) are included in current shift activity.</p>
                                <p>Planned = active operators assigned to current A/B shift team.</p>
                            </>
                        )}

                        <h3>OEE Availability</h3>
                        <p>Avg = current-shift availability from shift OEE rows (same source as Live Graphs shift line).</p>
                        <p>Current = latest aligned current-shift availability.</p>

                        <h3>Delay Bar</h3>
                        <p>Delay items combine completed measurements from SQL and ongoing measure tasks.</p>
                        <p>Each color shows share by item count; value in brackets is item count.</p>

                        <div style={{ border: '2px solid #ddd', borderRadius: 8, padding: '10px 10px 12px 10px', marginTop: 12 }}>
                            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 4 }}>
                                <strong>Details Table</strong>
                            </div>
                            <div style={{ display: 'flex', gap: 8, margin: '8px 0 10px 0' }}>
                                <button
                                    className='button--small'
                                    onClick={() => setInfoTab('delays')}
                                    style={{
                                        backgroundColor: infoTab === 'delays' ? '#04aa6d' : '#f2f2f2',
                                        color: infoTab === 'delays' ? '#fff' : '#222',
                                        border: infoTab === 'delays' ? '1px solid #04aa6d' : '1px solid #ccc',
                                        fontWeight: infoTab === 'delays' ? 700 : 500,
                                    }}
                                >
                                    Delays
                                </button>
                                <button
                                    className='button--small'
                                    onClick={() => setInfoTab('disconnected')}
                                    style={{
                                        backgroundColor: infoTab === 'disconnected' ? '#04aa6d' : '#f2f2f2',
                                        color: infoTab === 'disconnected' ? '#fff' : '#222',
                                        border: infoTab === 'disconnected' ? '1px solid #04aa6d' : '1px solid #ccc',
                                        fontWeight: infoTab === 'disconnected' ? 700 : 500,
                                    }}
                                >
                                    Disconnected
                                </button>
                                {shouldShowOperatorFeatures && (
                                    <button
                                        className='button--small'
                                        onClick={() => setInfoTab('operators')}
                                        style={{
                                            backgroundColor: infoTab === 'operators' ? '#04aa6d' : '#f2f2f2',
                                            color: infoTab === 'operators' ? '#fff' : '#222',
                                            border: infoTab === 'operators' ? '1px solid #04aa6d' : '1px solid #ccc',
                                            fontWeight: infoTab === 'operators' ? 700 : 500,
                                        }}
                                    >
                                        Operators
                                    </button>
                                )}
                            </div>

                            {infoTab === 'delays' && (
                                <>
                                    {sortedDelays.length === 0 ? (
                                        <p style={{ margin: '6px 0 0 0' }}>No delayed items in the current shift.</p>
                                    ) : (
                                        <table style={{ width: '100%', border: '1px solid #ddd' }}>
                                            <thead>
                                                <tr>
                                                    <th
                                                        style={{ textAlign: 'center', cursor: 'pointer' }}
                                                        onClick={() => toggleDelaySort('equipment')}
                                                    >
                                                        Equipment{getSortIndicator('equipment')}
                                                    </th>
                                                    <th style={{ cursor: 'pointer' }} onClick={() => toggleDelaySort('delay')}>
                                                        Delay{getSortIndicator('delay')}
                                                    </th>
                                                    <th
                                                        style={{ textAlign: 'center', cursor: 'pointer' }}
                                                        onClick={() => toggleDelaySort('delayFrom')}
                                                    >
                                                        From{getSortIndicator('delayFrom')}
                                                    </th>
                                                    <th style={{ textAlign: 'center' }}>To</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {sortedDelays.map((item, idx) => (
                                                    <tr key={`${item.equipment}-${item.delay}-${idx}`}>
                                                        <td style={{ textAlign: 'left', paddingLeft: 8 }}>{item.equipment}</td>
                                                        <td>{item.delay} min</td>
                                                        <td style={{ textAlign: 'left', paddingLeft: 8 }}>{formatDelayTime(item.delayFrom)}</td>
                                                        <td style={{ textAlign: 'left', paddingLeft: 8 }}>
                                                            {item.delayTo ? formatDelayTime(item.delayTo) : 'open'}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                </>
                            )}

                            {infoTab === 'disconnected' && (
                                <>
                                    {disconnectedMachines.length === 0 ? (
                                        <p style={{ margin: '6px 0 0 0' }}>No currently disconnected machines.</p>
                                    ) : (
                                        <table style={{ width: '100%', border: '1px solid #ddd' }}>
                                            <thead>
                                                <tr>
                                                    <th style={{ textAlign: 'center' }}>Equipment</th>
                                                    <th>Duration</th>
                                                    <th style={{ textAlign: 'center' }}>From</th>
                                                    <th style={{ textAlign: 'center' }}>To</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {disconnectedMachines.map((item, idx) => (
                                                    <tr key={`${item.equipment}-${item.disconnectedSince}-${idx}`}>
                                                        <td style={{ textAlign: 'left', paddingLeft: 8 }}>{item.equipment}</td>
                                                        <td>{item.disconnectedMinutes} min</td>
                                                        <td style={{ textAlign: 'left', paddingLeft: 8 }}>
                                                            {formatDelayTime(item.disconnectedSince)}
                                                        </td>
                                                        <td style={{ textAlign: 'left', paddingLeft: 8 }}>open</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                </>
                            )}

                            {shouldShowOperatorFeatures && infoTab === 'operators' && (
                                <table style={{ width: '100%', border: '1px solid #ddd' }}>
                                    <thead>
                                        <tr>
                                            <th style={{ textAlign: 'left', paddingLeft: 8 }}>Initials</th>
                                            <th style={{ textAlign: 'left', paddingLeft: 8 }}>Status</th>
                                            <th style={{ textAlign: 'left', paddingLeft: 8 }}>First Action</th>
                                            <th style={{ textAlign: 'left', paddingLeft: 8 }}>Latest Action</th>
                                            <th style={{ textAlign: 'left', paddingLeft: 8 }}>Actions</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {operatorStatusRows.length === 0 ? (
                                            <tr>
                                                <td colSpan={5} style={{ textAlign: 'left', padding: '8px' }}>
                                                    {notAvailable}
                                                </td>
                                            </tr>
                                        ) : (
                                            operatorStatusRows.map((row) => (
                                                <tr key={`${row.initials}-${row.status}`}>
                                                    <td
                                                        style={{
                                                            textAlign: 'left',
                                                            padding: '8px',
                                                            fontWeight: row.emphasize ? 700 : 500,
                                                            color: row.muted ? '#8a8a8a' : '#222',
                                                            backgroundColor: row.emphasize ? '#fff8e1' : 'transparent',
                                                        }}
                                                    >
                                                        {row.initials}
                                                    </td>
                                                    <td
                                                        style={{
                                                            textAlign: 'left',
                                                            padding: '8px',
                                                            fontWeight: row.emphasize ? 700 : 400,
                                                            color: row.emphasize ? '#8a5a00' : row.muted ? '#8a8a8a' : '#222',
                                                            backgroundColor: row.emphasize ? '#fff8e1' : 'transparent',
                                                        }}
                                                    >
                                                        {row.status}
                                                    </td>
                                                    <td
                                                        style={{
                                                            textAlign: 'left',
                                                            padding: '8px',
                                                            color: row.muted ? '#8a8a8a' : '#222',
                                                            backgroundColor: row.emphasize ? '#fff8e1' : 'transparent',
                                                        }}
                                                    >
                                                        {formatActionTime(row.firstActionTime)}
                                                    </td>
                                                    <td
                                                        style={{
                                                            textAlign: 'left',
                                                            padding: '8px',
                                                            color: row.muted ? '#8a8a8a' : '#222',
                                                            backgroundColor: row.emphasize ? '#fff8e1' : 'transparent',
                                                        }}
                                                    >
                                                        {formatActionTime(row.latestActionTime)}
                                                    </td>
                                                    <td
                                                        style={{
                                                            textAlign: 'left',
                                                            padding: '8px',
                                                            color: row.muted ? '#8a8a8a' : '#222',
                                                            backgroundColor: row.emphasize ? '#fff8e1' : 'transparent',
                                                        }}
                                                    >
                                                        {row.totalActions ?? notAvailable}
                                                    </td>
                                                </tr>
                                            ))
                                        )}
                                    </tbody>
                                </table>
                            )}
                        </div>
                    </div>
                </Dialog>
            )}
        </div>
    );
}

function formatDelayTime(dateInput: string | Date | null): string {
    if (!dateInput) return 'n/a';
    const d = new Date(dateInput);
    if (isNaN(d.getTime())) return 'n/a';

    const date = d.getFullYear() + '/' + String(d.getMonth() + 1).padStart(2, '0') + '/' + String(d.getDate()).padStart(2, '0');
    const time = String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0');
    return `${date} ${time}`;
}

function formatActionTime(dateInput: string | Date | null): string {
    if (!dateInput) return 'n/a';
    const d = new Date(dateInput);
    if (isNaN(d.getTime())) return 'n/a';

    return String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0');
}

function formatFull(dateInput: string | Date): string {
    if (!dateInput) return 'n/a';

    const d = new Date(dateInput);

    if (isNaN(d.getTime())) return 'n/a';

    const weekday = d.toLocaleDateString('en-US', { weekday: 'long' });
    const date = d.getFullYear() + '/' + String(d.getMonth() + 1).padStart(2, '0') + '/' + String(d.getDate()).padStart(2, '0');
    const time = String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0');
    return `${weekday}, ${date} ${time}`;
}
